import { configureStore } from '@reduxjs/toolkit';
import type { EPersona } from '@sharedInterfaces/EPersona';
import type IBusinessDTO from '@sharedInterfaces/IBusiness';
import type ICertificateDTO from '@sharedInterfaces/ICertificate';
import type { ILevel, IOU } from '@sharedInterfaces/ICompanySettings';
import type ICompetence from '@sharedInterfaces/ICompetence';
import type { IEmployeeDTO } from '@sharedInterfaces/IEmployee';
import type IEmployeeTalkTemplate from '@sharedInterfaces/IEmployeeTalkTemplate';
import type IOpportunity from '@sharedInterfaces/IOpportunity';
import type { IOrgUnitDetailDTO } from '@sharedInterfaces/IOrgUnit';
import type { IPermissions } from '@sharedInterfaces/IPermissions';
import type IProduct from '@sharedInterfaces/IProduct';
import type IProjectDTO from '@sharedInterfaces/IProject';
import type IRoleDTO from '@sharedInterfaces/IRole';
import type ISkillDetailDTO from '@sharedInterfaces/ISkill';
import type { ISkillCategoryDetailDTO } from '@sharedInterfaces/ISkillCategory';
import Business from '@src/Objects/Business';
import Certificate from '@src/Objects/Certificate';
import Competence from '@src/Objects/Competence';
import type Dashboard from '@src/Objects/Dashboard';
import Employee from '@src/Objects/Employee';
import EmployeeTalk from '@src/Objects/EmployeeTalk';
import { createSkillCategory } from '@src/Objects/factory';
import Opportunity from '@src/Objects/Opportunity';
import Product from '@src/Objects/Product';
import Project from '@src/Objects/Project';
import Role from '@src/Objects/Role';
import type { SkillCategory } from '@src/Objects/SkillCategory';
import SkillDetail from '@src/Objects/SkillDetail';
import { combineReducers, type Middleware } from 'redux';

import type IUser from '../interfaces/IUser';
import { businessReducer, EBusinessAction } from './reducer/businessReducer';
import { certificateReducer, ECertificateAction } from './reducer/certificatesReducer';
import chatAgentReducer from './reducer/chatAgentReducer';
import { clientReducer, EClientAction, type IClientState, initialClientState } from './reducer/clientReducer';
import { type CompanyState, companyReducer, ESetCompanyAction, initialCompanyState } from './reducer/companyReducer';
import { competenceReducer, ECompetenceAction } from './reducer/competenceReducer';
import {
    cookieSettingsReducer,
    ECookieSettingsAction,
    type ICookieSettingsState,
    initialCookieSettingsState,
} from './reducer/cookieReducer';
import { dashboardReducer, EDashboardAction } from './reducer/dashboardReducer';
import dialogsReducer, { type DialogsState } from './reducer/dialogsSlice';
import {
    EmployeeActionTypes,
    type EmployeeState,
    employeeReducer,
    initialEmployeeState,
} from './reducer/employeeReducer';
import {
    EmployeeSettingsActionTypes,
    type employeeSettingState,
    employeeSettingsReducer,
    initialEmployeeSettingsState,
    prepareGoal,
} from './reducer/employeeSettingsReducer';
import { EEmployeesAction, employeesReducer } from './reducer/employeesReducer';
import employeeTalksReducer, { EEmployeeTalkAction } from './reducer/employeeTalksReducer';
import { EETTAction, ettReducer } from './reducer/employeeTalkTemplatesReducer';
import { ELevelDefinitionAction, levelDefinitionReducer } from './reducer/levelDefinitionReducer';
import {
    initialNotificationsState,
    isNotificationAction,
    type NotificationsState,
    notificationsReducer,
} from './reducer/notificationsReducer';
import { EOUAction, OUsReducer } from './reducer/OUsReducer';
import { EOpportunityAction, opportunityReducer } from './reducer/opportunityRecuder';
import { EOrgUnitAction, orgunitReducer } from './reducer/orgUnitReducer';
import { initialPermissionsState, PermissionsActionTypes, permissionsReducer } from './reducer/permissionsReducer';
import { EPersonasAction, initialPersonasState, personasReducer } from './reducer/personasReducer';
import { EProductAction, productReducer } from './reducer/productReducer';
import { EProjectAction, projectReducer } from './reducer/projectReducer';
import { ERoleAction, roleReducer } from './reducer/rolesReducer';
import { ESkillCategorieAction, skillcategorieReducer } from './reducer/skillCategoriesReducer';
import { ESkillAction, skillReducer } from './reducer/skillReducer';
import { initialUserState, UserActionTypes, userReducer } from './reducer/userReducer';
import { type WindowSizeState, windowSizeReducer } from './reducer/windowSizeReducer';

export interface AppState {
    cookies: ICookieSettingsState;
    company: CompanyState;
    employee: EmployeeState;
    levelDefinition: ILevel[];
    OUs: IOU[];
    employeeSettings: employeeSettingState;
    user: IUser;
    windowSize: WindowSizeState;
    permissions: IPermissions;
    personas: EPersona[];
    skills: SkillDetail[];
    skillCategories: SkillCategory[];
    certificates: Certificate[];
    roles: Role[];
    competences: Competence[];
    products: Product[];
    employees: Employee[];
    orgUnits: IOrgUnitDetailDTO[];
    opportunity: Opportunity[];
    businesses: Business[];
    projects: Project[];
    client: IClientState;
    dialogs: DialogsState;
    notifications: NotificationsState;
    employeeTalkTemplates: IEmployeeTalkTemplate[];
    employeeTalks: EmployeeTalk[];
    dashboard: Dashboard[];
    chatAgent: ReturnType<typeof chatAgentReducer>;
}

const rootReducer = combineReducers({
    cookies: cookieSettingsReducer,
    company: companyReducer,
    employee: employeeReducer,
    levelDefinition: levelDefinitionReducer,
    OUs: OUsReducer,
    employeeSettings: employeeSettingsReducer,
    user: userReducer,
    windowSize: windowSizeReducer,
    permissions: permissionsReducer,
    personas: personasReducer,
    skills: skillReducer,
    skillCategories: skillcategorieReducer,
    certificates: certificateReducer,
    roles: roleReducer,
    competences: competenceReducer,
    products: productReducer,
    employees: employeesReducer,
    orgUnits: orgunitReducer,
    opportunity: opportunityReducer,
    businesses: businessReducer,
    projects: projectReducer,
    client: clientReducer,
    dialogs: dialogsReducer,
    notifications: notificationsReducer,
    employeeTalkTemplates: ettReducer,
    employeeTalks: employeeTalksReducer,
    dashboard: dashboardReducer,
    chatAgent: chatAgentReducer,
});

const localStorageMiddleware: Middleware = (store) => (next) => (action) => {
    const result = next(action);
    const state = store.getState();
    const save = async () => {
        // console.log(action.type, (Object.values(EETTAction).includes(action.type as EETTAction)));
        if (action.type === ESetCompanyAction.SET_COMPANY_ID)
            localStorage.setItem('companyId', state.company.companyId);
        else if (action.type === ESetCompanyAction.SET_COMPANY_NAME)
            localStorage.setItem('companyName', state.company.name);
        else if (action.type === ESetCompanyAction.SET_COMPANY_ALL_SKILLS)
            localStorage.setItem('allSkills', JSON.stringify(state.company.allSkills));
        else if (action.type === ESetCompanyAction.SET_COMPANY_ALL_COMPETENCES)
            localStorage.setItem('allCompetences', JSON.stringify(state.company.allCompetences));
        else if (action.type === ESetCompanyAction.SET_COMPANY_ALL_CERTIFICATES)
            localStorage.setItem('allCertificates', JSON.stringify(state.company.allCertificates));
        else if (action.type === ESetCompanyAction.SET_COMPANY_SMALL_ROLES)
            localStorage.setItem('smallRoles', JSON.stringify(state.company.allRoles));
        else if (action.type === ESetCompanyAction.SET_COMPANY_SKILL_CATEGORIES)
            localStorage.setItem('skillCategories', JSON.stringify(state.company.skillCategories));
        else if (action.type === ESetCompanyAction.SET_COMPANY_ALL_EMPLOYEES)
            localStorage.setItem('allEmployees', JSON.stringify(state.company.allEmployees));
        else if (action.type === ESetCompanyAction.SET_COMPANY_STYLE)
            localStorage.setItem('style', JSON.stringify(state.company.style));
        else if (action.type === ESetCompanyAction.SET_COMPANY_OPPORTUNITY_SETTINGS)
            localStorage.setItem('opportunitySettings', JSON.stringify(state.company.opportunitySettings));
        else if (action.type === ESetCompanyAction.SET_COMPANY_VERSIONS)
            localStorage.setItem('versions', JSON.stringify(state.company.versions));
        else if (action.type === ESetCompanyAction.SET_COMPANY_VERSION)
            localStorage.setItem('versions', JSON.stringify(state.company.versions));
        else if (action.type === ESetCompanyAction.SET_COMPANY_FEATURE_FLAGS)
            localStorage.setItem('featureFlags', JSON.stringify(state.company.featureFlags));
        else if (Object.values(EmployeeActionTypes).includes(action.type as EmployeeActionTypes))
            localStorage.setItem('employee', JSON.stringify(state.employee));
        else if (Object.values(ELevelDefinitionAction).includes(action.type as ELevelDefinitionAction))
            localStorage.setItem('levelDefinitions', JSON.stringify(state.levelDefinition));
        else if (Object.values(EOUAction).includes(action.type as EOUAction))
            localStorage.setItem('OUs', JSON.stringify(state.OUs));
        else if (Object.values(EmployeeSettingsActionTypes).includes(action.type as EmployeeSettingsActionTypes))
            localStorage.setItem('employeeSettings', JSON.stringify(state.employeeSettings));
        else if (Object.values(UserActionTypes).includes(action.type as UserActionTypes))
            localStorage.setItem('user', JSON.stringify(state.user));
        else if (Object.values(PermissionsActionTypes).includes(action.type as PermissionsActionTypes))
            localStorage.setItem('permissions', JSON.stringify(state.permissions));
        else if (Object.values(ESkillAction).includes(action.type as ESkillAction))
            localStorage.setItem('skills', JSON.stringify(state.skills));
        else if (Object.values(ESkillCategorieAction).includes(action.type as ESkillCategorieAction))
            localStorage.setItem('skillCategories', JSON.stringify(state.skillCategories));
        else if (Object.values(ECertificateAction).includes(action.type as ECertificateAction))
            localStorage.setItem('certificates', JSON.stringify(state.certificates));
        else if (Object.values(ERoleAction).includes(action.type as ERoleAction))
            localStorage.setItem('roles', JSON.stringify(state.roles));
        else if (Object.values(ECompetenceAction).includes(action.type as ECompetenceAction))
            localStorage.setItem('competences', JSON.stringify(state.competences));
        else if (Object.values(EProductAction).includes(action.type as EProductAction))
            localStorage.setItem('products', JSON.stringify(state.products));
        else if (Object.values(EEmployeesAction).includes(action.type as EEmployeesAction))
            localStorage.setItem('employees', JSON.stringify(state.employees));
        else if (Object.values(EOrgUnitAction).includes(action.type as EOrgUnitAction))
            localStorage.setItem('orgUnits', JSON.stringify(state.orgUnits));
        else if (Object.values(EOpportunityAction).includes(action.type as EOpportunityAction))
            localStorage.setItem('opportunity', JSON.stringify(state.opportunity));
        else if (Object.values(EBusinessAction).includes(action.type as EBusinessAction))
            localStorage.setItem('businesses', JSON.stringify(state.businesses));
        else if (Object.values(EProjectAction).includes(action.type as EProjectAction))
            localStorage.setItem('projects', JSON.stringify(state.projects));
        else if (Object.values(EClientAction).includes(action.type as EClientAction))
            localStorage.setItem('client', JSON.stringify(state.client));
        else if (Object.values(ECookieSettingsAction).includes(action.type as ECookieSettingsAction))
            localStorage.setItem('cookies', JSON.stringify(state.cookies));
        else if (isNotificationAction(action.type))
            localStorage.setItem('notifications', JSON.stringify(state.notifications));
        else if (Object.values(EETTAction).includes(action.type as EETTAction))
            localStorage.setItem('employeeTalkTemplates', JSON.stringify(state.employeeTalkTemplates));
        else if (Object.values(EEmployeeTalkAction).includes(action.type as EEmployeeTalkAction))
            localStorage.setItem('employeeTalks', JSON.stringify(state.employeeTalks));
        else if (Object.values(EDashboardAction).includes(action.type as EDashboardAction))
            localStorage.setItem('dashboard', JSON.stringify(state.dashboard));
        else if (Object.values(EPersonasAction).includes(action.type as EPersonasAction))
            localStorage.setItem('personas', JSON.stringify(state.personas));
    };
    save();
    return result;
};

const loadState = () => {
    try {
        const empSettings = JSON.parse(
            localStorage.getItem('employeeSettings') || JSON.stringify(initialEmployeeSettingsState),
        ) as employeeSettingState;

        return {
            company: {
                companyId: localStorage.getItem('companyId') || initialCompanyState.companyId,
                name: localStorage.getItem('companyName') || initialCompanyState.name,
                allCompetences: JSON.parse(
                    localStorage.getItem('allCompetences') || JSON.stringify(initialCompanyState.allCompetences),
                ),
                allSkills: JSON.parse(
                    localStorage.getItem('allSkills') || JSON.stringify(initialCompanyState.allSkills),
                ),
                allCertificates: JSON.parse(
                    localStorage.getItem('allCertificates') || JSON.stringify(initialCompanyState.allCertificates),
                ),
                allRoles: JSON.parse(
                    localStorage.getItem('smallRoles') || JSON.stringify(initialCompanyState.allRoles),
                ),
                skillCategories: JSON.parse(
                    localStorage.getItem('skillCategories') || JSON.stringify(initialCompanyState.skillCategories),
                ),
                allEmployees: JSON.parse(
                    localStorage.getItem('allEmployees') || JSON.stringify(initialCompanyState.allEmployees),
                ),
                style: JSON.parse(localStorage.getItem('style') || JSON.stringify(initialCompanyState.style)),
                opportunitySettings: JSON.parse(
                    localStorage.getItem('opportunitySettings') ||
                        JSON.stringify(initialCompanyState.opportunitySettings),
                ),
                versions: JSON.parse(localStorage.getItem('versions') || JSON.stringify(initialCompanyState.versions)),
                featureFlags: JSON.parse(
                    localStorage.getItem('featureFlags') || JSON.stringify(initialCompanyState.featureFlags),
                ),
            },
            employee: JSON.parse(localStorage.getItem('employee') || JSON.stringify(initialEmployeeState)),
            levelDefinition: JSON.parse(localStorage.getItem('levelDefinitions') || '[]'),
            OUs: JSON.parse(localStorage.getItem('OUs') || '[]'),
            employeeSettings: {
                ...empSettings,
                goals: prepareGoal(empSettings.goals),
                competenceSettings: {
                    ...empSettings.competenceSettings,
                    wantedCompetences: prepareGoal(empSettings.competenceSettings.wantedCompetences),
                },
            },
            user: JSON.parse(localStorage.getItem('user') || JSON.stringify(initialUserState)),
            permissions: JSON.parse(localStorage.getItem('permissions') || JSON.stringify(initialPermissionsState)),
            skills: (JSON.parse(localStorage.getItem('skills') || JSON.stringify([])) as ISkillDetailDTO[]).map(
                (s) => new SkillDetail(s),
            ),
            skillCategories: (
                JSON.parse(localStorage.getItem('skillCategories') || JSON.stringify([])) as ISkillCategoryDetailDTO[]
            ).map((s) => createSkillCategory(s)),
            certificates: (
                JSON.parse(localStorage.getItem('certificates') || JSON.stringify([])) as ICertificateDTO[]
            ).map((s) => new Certificate(s)),
            roles: (JSON.parse(localStorage.getItem('roles') || JSON.stringify([])) as IRoleDTO[]).map(
                (s) => new Role(s),
            ),
            competences: (JSON.parse(localStorage.getItem('competences') || JSON.stringify([])) as ICompetence[]).map(
                (s) => new Competence(s),
            ),
            products: (JSON.parse(localStorage.getItem('products') || JSON.stringify([])) as IProduct[]).map(
                (s) => new Product(s),
            ),
            employees: (JSON.parse(localStorage.getItem('employees') || JSON.stringify([])) as IEmployeeDTO[]).map(
                (s) => new Employee(s),
            ),
            orgUnits: JSON.parse(localStorage.getItem('orgUnits') || JSON.stringify([])) as IOrgUnitDetailDTO[],
            opportunity: (JSON.parse(localStorage.getItem('opportunity') || JSON.stringify([])) as IOpportunity[]).map(
                (s) => new Opportunity(s),
            ),
            businesses: (JSON.parse(localStorage.getItem('businesses') || JSON.stringify([])) as IBusinessDTO[]).map(
                (s) => new Business(s),
            ),
            projects: (JSON.parse(localStorage.getItem('projects') || JSON.stringify([])) as IProjectDTO[]).map(
                (s) => new Project(s),
            ),
            client: {
                ...JSON.parse(localStorage.getItem('client') || JSON.stringify(initialClientState)),
                offline: false,
            },
            cookies: JSON.parse(localStorage.getItem('cookies') || JSON.stringify(initialCookieSettingsState)),
            dialogs: { dialogs: [] },
            notifications: JSON.parse(
                localStorage.getItem('notifications') || JSON.stringify(initialNotificationsState),
            ),
            employeeTalkTemplates: JSON.parse(localStorage.getItem('employeeTalkTemplates') || JSON.stringify([])),
            employeeTalks: (JSON.parse(localStorage.getItem('employeeTalks') || JSON.stringify([])) as any[]).map(
                (t) => new EmployeeTalk(t),
            ),
            personas: JSON.parse(localStorage.getItem('personas') || JSON.stringify(initialPersonasState)),
            chatAgent: { chatList: [], isLoading: false },
        };
    } catch (ex) {
        console.error(ex);
        const client = localStorage.getItem('client');
        const cookies = localStorage.getItem('cookies');
        localStorage.clear();
        if (client) {
            localStorage.setItem('client', client);
        }
        localStorage.setItem('cookies', cookies || JSON.stringify(initialCookieSettingsState));
        location.reload();
    }
    return {};
};

const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
        }).concat(localStorageMiddleware),
    preloadedState: loadState(),
});
export default store;
